import { useBPLogger } from '@/composables/business-process-logger';
import type { MatrixOrder } from '@/models/matrix-order.model';
import type { MatrixProduct } from '@/models/matrix-product.model';
import {
  CreateDraftDto,
  CreateDraftLineDto,
  DraftInitInfoDto,
  DraftModel,
  OrderSummaryDto,
  OrderType,
  OriginApp,
  ValidatorResult,
} from '@onrunning/backstage-models';
import { useOrderHeadersStore } from '../stores/order-headers';
import { CrudService } from './crud.service';

export class DraftsService {
  private crudService: CrudService<any>;
  private readonly setRefreshHeaders: () => void;
  private submitOrderBPLog;
  private addToCartBPLog;

  constructor() {
    const { setRefreshHeaders } = useOrderHeadersStore();
    this.setRefreshHeaders = setRefreshHeaders;
    this.crudService = new CrudService<any>('api/v2/drafts');
    const { submitOrderBPLog, addToCartBPLog } = useBPLogger();
    this.submitOrderBPLog = submitOrderBPLog;
    this.addToCartBPLog = addToCartBPLog;
  }

  public get(id: string, accountId: string, withRelatedDrafts = false): Promise<ValidatorResult<DraftModel>> {
    return this.crudService.get(id, { params: { accountId, withRelatedDrafts } });
  }

  public async update(draft: DraftModel, accountId: string): Promise<ValidatorResult<DraftModel>> {
    const result = await this.crudService.put(draft.id, draft, { params: { accountId } });

    this.setRefreshHeaders();

    return result;
  }

  public async remove(id: string, accountId: string): Promise<ValidatorResult<DraftModel>> {
    const result = await this.crudService.remove(id, { params: { accountId } });

    this.setRefreshHeaders();

    return result;
  }

  public async submit(draft: DraftModel, accountId: string): Promise<ValidatorResult<DraftModel>> {
    const result = await this.crudService.do('put', `submit/${draft.id}?${this.getAccountUrlParam(accountId)}`, draft);

    const logData = {
      order_number: result?.model?.orderNumber,
      subflow: 'order_creation',
    };

    if (result.isValid()) {
      console.log(this.submitOrderBPLog(true, logData));
    } else {
      console.log(
        this.submitOrderBPLog(false, {
          ...logData,
          error_message: result?.errors?.map((e) => e.error).join(','),
        }),
      );
    }
    this.setRefreshHeaders();

    return result;
  }

  public getPrices(draftId: string, accountId: string, productId?: string): Promise<ValidatorResult<DraftModel>> {
    const line = productId ? `/lines/${productId}` : '';

    return this.crudService.do('get', `${draftId}/prices${line}?${this.getAccountUrlParam(accountId)}`);
  }

  public async getInitInfo(accountId: string): Promise<ValidatorResult<DraftInitInfoDto>> {
    return this.crudService.doWithCustomType<DraftInitInfoDto>(
      'get',
      `create/init-info?${this.getAccountUrlParam(accountId)}`,
    );
  }

  public getOrderSummary(accountId: string, orderNumbers: string[]): Promise<ValidatorResult<OrderSummaryDto[]>> {
    const queryParams = new URLSearchParams();

    queryParams.set('accountId', accountId);

    return this.crudService.do('post', `summary?${queryParams}`, { orderNumbers });
  }

  public async create(
    createDraftDto: CreateDraftDto,
    accountId: string,
  ): Promise<ValidatorResult<CreateDraftDto & { id?: string; orderNumber?: string }>> {
    const result = await this.crudService.post(createDraftDto, { params: { accountId } });

    const logData = {
      order_number: result?.model?.orderNumber,
      draft_id: result?.model?.id,
      item_numbers: result?.model?.lines?.map((line: CreateDraftLineDto) => line.itemNumber),
      subflow: 'create_draft',
    };

    if (result.isValid()) {
      console.log(this.addToCartBPLog(true, logData));
    } else {
      const error_message = result?.errors?.map((e) => e.error).join(',');
      console.log(this.addToCartBPLog(false, { ...logData, error_message }));
    }

    this.setRefreshHeaders();

    return result;
  }

  async createDraftFromMatrixOrder(
    matrixOrder: MatrixOrder,
    contactId: string | undefined,
    orderType: OrderType,
    selectedAccountId: string,
    isSalesRep: boolean,
    requestDateString: string,
    multiplePreOrdersGroupId?: string,
  ): Promise<DraftModel | undefined> {
    const matrixProducts = matrixOrder.matrixProducts.filter((matrixProduct) => matrixProduct.total !== 0);

    const createDraftDto: CreateDraftDto = {
      origin: OriginApp.Backstage,
      contactId: contactId,
      type: orderType,
      requestedDate: requestDateString,
      isSalesRep: isSalesRep,
      lines: matrixProducts.map((matrixProduct) => this.mapMatrixProductToDraftLine(matrixProduct)),
      groupId: multiplePreOrdersGroupId,
    };

    const result = await this.create(createDraftDto, selectedAccountId);

    if (result.isValid() && result.model?.id) {
      matrixOrder.orderId = result.model?.id;

      // ToDo if the create would return the order on demand then we could avoid this second request
      return (await this.get(result.model?.id, selectedAccountId)).model;
    }
  }

  async createDraft(draft: CreateDraftDto, accountId: string): Promise<ValidatorResult<DraftModel>> {
    const result = await this.create(draft, accountId);

    if (result.isValid() && result.model?.id) {
      return await this.get(result.model?.id, accountId);
    }

    return result as unknown as ValidatorResult<DraftModel>;
  }

  public async getSpecificOrders(accountId: string, orderNumbers: string[]): Promise<ValidatorResult<DraftModel[]>> {
    return await this.crudService.do('get', `selected-drafts?accountId=` + accountId + `&orderNumbers=` + orderNumbers);
  }

  private mapMatrixProductToDraftLine(matrixProduct: MatrixProduct): CreateDraftLineDto {
    const line = new CreateDraftLineDto();
    line.barcode = matrixProduct.productId;
    line.productGroupName = matrixProduct.productGroupName;
    line.quantity = matrixProduct.total;
    line.productName = `${matrixProduct.productName} ${matrixProduct.sizeId}`;
    line.itemNumber = matrixProduct.productGroupNumber;

    return line;
  }

  private getAccountUrlParam(accountId: string) {
    const params = new URLSearchParams();

    params.set('accountId', accountId);

    return params;
  }
}
